<script setup lang="ts">
import type { AcademicArticleSection } from '~/utils/solr/academic-article'
import type { BookAndArticle } from '~/utils/solr/literature'
import { computed } from 'vue'
import { useArticleInteractions } from '~/composables/useArticleInteractions'
import { useArticleReferences } from '~/composables/useArticleLawReference'
import { useVisibleSectionSync } from '~/composables/useVisibleSectionSync'

const props = defineProps<{
  articleId: string
  article?: BookAndArticle | null
  sections?: AcademicArticleSection[]
}>()

const emit = defineEmits<{
  visibleSectionChanged: [sectionId: string | undefined]
  referenceOpen: [trigger: HTMLElement]
}>()

// Persist selected law reference for this article
const { addLawNodeIdToReferences, removeLawIdFromReferences, lawReferenceNodeIds } = useArticleReferences(computed(() => props.articleId))

// Use the new composable for article interactions
const { contentRoot } = useArticleInteractions(addLawNodeIdToReferences, removeLawIdFromReferences, lawReferenceNodeIds, trigger => emit('referenceOpen', trigger))

const publisher = computed(() => props.article?.publisher?.[0] ?? '')
const year = computed(() => props.article?.year ?? '')

const sectionIds = computed(() => (props.sections ?? []).map(section => section.id))
useVisibleSectionSync({
  sectionIds,
  strategy: 'top-marker',
  topMarkerOffset: 80,
  onChange: sectionId => emit('visibleSectionChanged', sectionId),
})
</script>

<template>
  <div>
    <div ref="contentRoot" class="max-w-4xl mx-auto md:px-m">
      <div class="sticky top-0 max-md:top-[52px] z-10 bg-surface-elevated">
        <slot name="header" />
      </div>

      <template v-if="(props.sections || []).length > 0">
        <template v-for="section in props.sections" :key="section?.id">
          <div
            v-if="section"
            :id="section.id"
            data-citation-source="literature"
            data-marking-content-type="academic-article"
            :data-marking-work-id="articleId"
            :data-marking-document-id="articleId"
            :data-marking-unit-id="section.id"
            :data-marking-unit-title="section.title"
            :data-citation-date-of-publication="article?.dateOfPublication"
            :data-citation-title="article?.title || ''"
            :data-citation-authors="article?.authorName?.join(',')"
            :data-citation-publisher="publisher"
            :data-citation-year="year"
          >
            <div class="flex">
              <div class="grow">
                <p v-if="section.title && section.showHeader" class="title-m mb-m dark:text-white" data-marking-content>
                  {{ section.title }}
                </p>
                <BodyText
                  v-if="section.bodytext"
                  source="ibexa"
                  :html="section.bodytext"
                />
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped lang="postcss">
:deep(.body-text) {
  h1 {
    @apply font-heading text-2xl sm:text-4xl font-semibold dark:text-white;
  }
  h2 {
    @apply font-body text-xl sm:text-lg font-bold mb-m dark:text-white;
  }
  h3 {
    @apply font-body text-xl sm:text-base font-bold mb-m dark:text-white;
  }
  h4 {
    @apply font-body text-xl sm:text-base font-bold italic mb-m dark:text-white;
  }
  h5 {
    @apply font-body text-xl sm:text-base font-bold italic mb-m dark:text-white;
  }
  button.reference {
    @apply bg-transparent p-0 border-0 underline cursor-pointer inline-flex;
  }

  .accordion {
    @apply rounded-md overflow-hidden mb-4 bg-surface-primary hover:bg-blurple-100;
  }
  .accordion h2 {
    @apply p-s py-s text-xl sm:text-lg font-bold font-body cursor-pointer transition-colors duration-200 flex items-center justify-between relative;
  }
  .accordion h2 [data-accordion-chevron='true'] {
    @apply transition-transform duration-200 ease-in-out rotate-0;
  }
  .accordion.open {
    @apply bg-surface-primary hover:bg-blurple-100;
  }
  .accordion.open h2 [data-accordion-chevron='true'] {
    @apply rotate-180;
  }
  .accordion .accordion-content {
    @apply hidden px-4 py-3;
  }
  .accordion.open .accordion-content {
    @apply block;
  }

  .information span {
    @apply pl-[1px] pr-[2px];
  }
  .information > span:first-child {
    @apply relative -top-[2px] cursor-pointer rounded transition-colors duration-300;
    @apply hover:bg-surface-secondary active:bg-blurple-500 active:text-white;
  }

  .information > span:first-child.tooltip-pinned {
    @apply bg-blurple-500 text-white;
    @apply hover:bg-surface-secondary hover:text-blurple-500;
  }
  .information > span:nth-child(2) {
    @apply hidden;
  }

  span sup {
    @apply cursor-pointer border border-blurple-500 rounded-md px-1 py-[1px];
    @apply hover:bg-surface-secondary active:bg-surface-invert active:text-white;
  }
}
</style>
